import { AxiosError } from 'axios';
import { ChangeEvent, useState } from 'react';
import { useTheme } from 'styled-components';
import { handleAxiosError } from 'src/api';
import { FormInput, handleErrorToast } from 'src/components/molecules';
import { AccountType, FormInputVariant, FormState } from 'src/types';
import * as Validator from 'validator';
import axios from '../../axios';
import {
  ImageContainer,
  SuccessContainer,
  SuccessHeader,
  SuccessText,
} from './ConfirmationContainer.styles';
import LoginContainer from './LoginContainer';

interface Props {
  header: string;
  description: string;
  emailEndpoint: string; // Endpoint for the axios query
  sendEmailBtnText: string; // Text to display on the submit button
  successfulEmailSentHeader: string; // Message displayed on successful submit
  successfulEmailSentText: string; // Message displayed on successful submit
  isAppAccount?: boolean;
}

export const SendEmailContainer = (props: Props) => {
  const theme = useTheme();
  const emptyStateSrc = theme.isVaultVariant
    ? '/assets/empty-state.svg'
    : '/assets/empty-state-atlas.svg';
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const onChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newEmail = event.currentTarget.value;
    const isValid = Validator.isEmail(newEmail);
    setEmail(newEmail);
    setIsEmailValid(isValid);
  };

  const sendEmail = async () => {
    if (isEmailValid) {
      try {
        setIsSending(true);
        await axios.post(props.emailEndpoint, {
          username: email,
        });
        setIsEmailSent(true);
        setIsSending(false);
      } catch (error) {
        const errors = handleAxiosError(
          error as AxiosError<{ errors: { message: string }[] }>,
        );
        if (Array.isArray(errors) && errors[0].message) {
          handleErrorToast({ toastMessage: errors[0].message });
        } else {
          handleErrorToast({ toastMessage: 'Could not send email.' });
        }
      }
    } else {
      handleErrorToast({ toastMessage: 'Invalid Email Address' });
    }
  };

  const isButtonDisabled =
    !email || !email.length || !isEmailValid || isSending;

  return (
    <>
      <LoginContainer
        header={isEmailSent ? '' : props.header}
        description={isEmailSent ? '' : props.description}
        includeForm={!isEmailSent}
        buttonText={props.sendEmailBtnText}
        buttonOnClick={sendEmail}
        buttonDisabled={isButtonDisabled}
        kind={props.isAppAccount ? AccountType.App : AccountType.Portal}
        data-testid="send-email"
      >
        {isEmailSent ? (
          <SuccessContainer data-testid="send-email-success-message">
            <ImageContainer>
              <img src={emptyStateSrc} alt="Success" width={'100%'} />
            </ImageContainer>
            <SuccessHeader>{props.successfulEmailSentHeader}</SuccessHeader>
            <SuccessText>
              {props.successfulEmailSentText} to {email}
            </SuccessText>
          </SuccessContainer>
        ) : (
          <FormInput
            label="Email"
            variant={FormInputVariant.INPUT}
            placeholder="Email"
            data-testid="send-email"
            state={isButtonDisabled ? FormState.DEFAULT : FormState.SUCCESS}
            onChange={onChange}
            type="email"
            value={email}
            enterKeyAction={() => sendEmail()}
            $removeMarginBottom={true}
          />
        )}
      </LoginContainer>
    </>
  );
};
